const QUESTION_TYPES = {
  1: {
    name: 'Email',
    prefix: 'E',
  },
  2: {
    name: 'Instant Message',
    prefix: 'IM',
  },
};

const PAGE_TOOLTIPS = {
  pageHeader: {
    skills: `This is where you create the skills that can be measured in your Inbox version. Simply enter the skills you want on this page and save. You can return to this area to edit or add more skills at any time.`,
  },
  milestones: {
    1: `Welcome to the Authoring Platform! You’ve checked off the first Milestone of your journey.`,
    2: `Let’s get you building! Clicking the Add New Project button on your dashboard and naming your project on the Create Inbox screen is the first step on your authoring journey.`,
    3: `What are you looking to measure? Skills are the backbone of Inbox simulations. Creating and defining the skills that your participants will be measured on will allow you to tailor the experience to your goals.`,
    4: `Characters are who your participants interact with. As you create more characters, think about the ways you can develop and insert personality into your Inbox simulation.`,
    5: `Emails are possibly the most important piece of your Inbox simulation. Create your first one to start building a foundation for your project.`,
    6: `Grab some ready-to-use content that you can copy and paste into your project. You can access all QuickStart Materials in the Resource Library on the Dashboard or directly on the Skills, Scenario, or Drive pages.`,
    7: `Start utilizing Skill points! You’ll earn this milestone after adding points to any response in your project.`,
    8: `You’ll likely want to stagger when your emails and messages are delivered to the participant. Make an adjustment to the Schedule panel within Email Settings to modify the timing and flow of your Inbox simulation.`,
    9: `Make a change on the Gadgets page. The Gadgets page hosts features like scoring methodology, timer settings, and many more!`,
    10: `Want to see your Inbox in action as a participant? Click the “Preview Experience” link in your Inbox tab. The preview function will help you get a feel for how your triggers and timing are playing. You can use this to evaluate edits as you make them.`,
    11: `Add some supplemental content into your Inbox. Upload a file as an attachment to one of your emails. Attachments can be images, documents, or videos. We recommend that the character references the attachment in the email body (i.e. “see attached”) for best results.`,
    12: `Start having people go through your Inbox and populate your data set! At this point, you should start building a picture of your Inbox and your participants.`,
  },
  scenarioPage: {
    role: 'This is where you explain the role that your users will assume in the Inbox. This content will display to the participant in the steps before they begin the Inbox – any information that will help them understand their role is encouraged. This is a great opportunity to engage and immerse your participants into your narrative. Consider: <i>What is their title, where do they work, what are there responsibilities?</i>',
    pdf: 'The Scenario page presents an opportunity to immerse and engage the users before they enter the Inbox – this is one more way to do that. Are there details that you want to share that don’t fit into the Situation or Participant Role field? Are there any visuals you want to display that will help prepare your users? Upload a PDF here and it will be displayed before the Inbox, directly under the Participant Role information.<br><br>Some authors use this area to upload an overview of the participant’s industry or company – others have designed fake webpages that display key information – the utility of the PDF is entirely up to you.',
  },
  dashboard: {
    featuredProject:
      'Featured Projects are a collection of templates and sample Inbox content to get you moving. Click the Learn More button for each to learn how you can use them!',
  },
  builderPage: {
    timeToComplete:
      'This number represents the default amount of time for how long participants will have to complete the Inbox. This can be adjusted by the Inbox’s administrator on the Course Settings page.',
    themeModalBanner: `This is the top navigation bar of the Inbox. The chosen color here will apply to all participants’ Inbox interface.`,
    themeModalLogo: `This is the logo participants see on the top left corner of their Inbox. PNG & JPEG files are accepted. For best results, we recommend a logo size of 200px high and 800px wide. A logo larger than this will be automatically resized to fit within those dimensions.`,
    themeModalTracker: `This is the progress bar when participants go through the onboarding process.`,
    themeModalBtn: `This is the main button participants interact with throughout the Inbox experience, for example, the “Respond” button in the emails; the “Next” button during onboarding.`,
    responseType: `Emails and Instant Messages can have different response types. Multiple Choice will require the participant to choose from the available response options provided. Multi-Select allows the participant to select as many response options as required. Open Text will require the participant to manually type and submit a response`,
    timeDelay: `
        Schedule determines when each email or instant message is “sent” to the participant.
        <br>
        <br>
        If the email or instant message is independent of a user action, it will default to display at the start of the inbox. In this instance, you can adjust the scheduled delivery by setting a delay. An email with a delay of 2m 30s means that the email will be “sent” to the user 2 minutes and 30 seconds after the start of the inbox.
        <br>
        <br>
        If the email or instant message is triggered by a user selecting a response, any delays are dependent on the settings of that specific response. You will want to return to that response to adjust timing.
      `,
    feedback:
      'The Participant Feedback feature is an opportunity to debrief users on the impact and theory behind moments in the Inbox. If you check the box, content you include will be compiled into a post-Inbox report. All details of the email or IM will be included to add context to your comments. This provides an opportunity to debrief the impacts of participant decision. How you explain the moment is up to you, but we recommend a generalized approach that highlights which responses are more effective than others and why.',
    tags: `Tags are an organizational tool for the authoring process. They will have no impact on the participant experience. You can use tags to help organize characters, narratives, or any other theme. To create or assign tags to an interaction, click “Add Tag”. In the tag pop-up, you can choose from existing options, or you can create a new tag by adding a title and selecting from one of five color choices. Interactions with tags assigned can be sorted in your Inbox – click the filter dropdown (next to the Add email button) to organize your view.
      <br>
      <br>
      To un-assign a tag from an email, click the “x” next to the tag. If you’d like to edit or delete a tag that you have created, click the Tag Settings icon.`,
    scoring:
      'This is where you can add scores to each response option – applying assessment points for the skills that you created. Click “Add Skill” to assign points. If the situation fits, consider adding multiple scores to some responses – this is a good way to simulate complex decisions that yield both positive and negative impacts.<br><br>Remember, you can return to the Skills page and create additional scoring criteria at any point.',
    trigger: `Triggers allow you to create dynamic storylines by creating decision branches and threads. When you add a trigger to a response option, you are creating a link to another email or instant message. Now, when your participant selects that particular response, the follow-up email (or IM) is automatically triggered. This allows you to design experiences that react dynamically to the decisions that participants make. Like scoring, responses can be assigned multiple triggers.
      <br>
      <br>
      When you add a trigger, you can select between emails and IMs that you have already created, or create a new interaction directly from the trigger function. You can control the timing of the experience by adjusting the Schedule fields. For example, if you set an email trigger to appear in 1 minute and 30 seconds, that timer will start the moment the participant chooses that response. A minute and a half later, the selected email is “sent” to the participant.`,
    driveFile: `The Drive is where you can house additional information that your participants may need to make decisions in the Inbox. You can upload Documents (PDFs), Images (.PNGs), and Videos (.mp4) to the Drive. Unlike email attachments, Drive files are available to the participant throughout the Inbox. Things like organizational charts, reports, or any other materials that will provide context for multiple interactions – are recommended for upload to the Drive. 
      <br>
      <br>
      <i>Note: Video files need to be uploaded online prior to being added to your project.</i>
      <br>
      <br>
      Every material that you upload must be housed in a folder. You can create additional Drive folders by selecting “Create Folder”. Inbox projects start with an initial folder called “Company Files” – you can edit the name of the folder of delete it, but there must be at least one active folder if you want to upload files to the Drive.`,
  },
};

const THEME_COLORS = [
  {
    backgroundColor: '#b1ddd0',
    color: '#000',
  },
  {
    backgroundColor: '#c4c1e0',
    color: '#000',
  },
  {
    backgroundColor: '#f7abad',
    color: '#000',
  },
  {
    backgroundColor: '#fdfad9',
    color: '#000',
  },
  {
    backgroundColor: '#90d1bc',
    color: '#000',
  },
  {
    backgroundColor: '#a2a1cf',
    color: '#000',
  },
  {
    backgroundColor: '#f38485',
    color: '#000',
  },
  {
    backgroundColor: '#fbf6b5',
    color: '#000',
  },
  {
    backgroundColor: '#6ec5a6',
    color: '#000',
  },
  {
    backgroundColor: '#7e81be',
    color: '#000',
  },
  {
    backgroundColor: '#f05d5f',
    color: '#000',
  },
  {
    backgroundColor: '#faf291',
    color: '#000',
  },
  {
    backgroundColor: '#1e7658',
    color: '#fff',
  },
  {
    backgroundColor: '#2a3167',
    color: '#fff',
  },
  {
    backgroundColor: '#ab1f23',
    color: '#fff',
  },
  {
    backgroundColor: '#b8af31',
    color: '#000',
  },
  {
    backgroundColor: '#538970',
    color: '#000',
  },
  {
    backgroundColor: '#4b4b79',
    color: '#fff',
  },
  {
    backgroundColor: '#b94e3b',
    color: '#fff',
  },
  {
    backgroundColor: '#acac56',
    color: '#000',
  },
  {
    backgroundColor: '#799f8b',
    color: '#000',
  },
  {
    backgroundColor: '#6f6b90',
    color: '#fff',
  },
  {
    backgroundColor: '#c6745e',
    color: '#000',
  },
  {
    backgroundColor: '#d2cfa2',
    color: '#000',
  },
];

export default {
  QUESTION_TYPES,
  PAGE_TOOLTIPS,
  THEME_COLORS,
};
